// Classification helpers for failed lazy `import()`s.
//
// Deliberately dependency-free: `main.ts` imports this before Pinia is installed, and pulling a
// store (or anything that pulls a store) in at that point would drag half the app into the eager
// graph just to read an error message.

// Each engine words a failed module fetch differently, and the two MIME-rejection variants do not
// mention "dynamically imported" at all — those are what a chunk served as `text/plain`, or as
// `text/html` by an SPA fallback, looks like from JS. All matched lower-cased.
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module", // Chromium
  "error loading dynamically imported module", // Firefox
  "importing a module script failed", // Safari
  "unable to preload css for", // Vite's own CSS preload failure
  "expected a javascript-or-wasm module script", // wrong Content-Type
  "strict mime type checking is enforced", // wrong Content-Type (Chromium wording)
];

/**
 * Whether an error is a chunk that failed to load, as opposed to a runtime error thrown by code
 * inside a chunk that loaded fine. Matched on the message because there is no structured field
 * for it — the browser rejects the import with a plain `TypeError`.
 */
export function isDynamicImportError(error: unknown): boolean {
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message !== "string") {
    return false;
  }
  const normalizedMessage = message.toLowerCase();
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => normalizedMessage.includes(pattern));
}

/**
 * Pull the module URL out of a dynamic-import failure so logs name the culprit. Every engine
 * embeds it in the message text; returns null rather than guessing when none is present.
 */
export function getFailedModuleUrl(error: unknown): string | null {
  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message !== "string") {
    return null;
  }
  return message.match(/https?:\/\/[^\s"')]+/)?.[0] ?? null;
}
